import React, { useContext, useEffect, useMemo, useState } from "react";
import { Button, OverlayTrigger, Tooltip } from "react-bootstrap";
import DataTable from "react-data-table-component";
import { gql, useQuery } from "@apollo/client";
import { useNavigate } from "react-router-dom";
import Spinner from "../../components/Spinner";
import StarekContext from "../../context/StarekContext";
import AddRolesWizard from "../../components/Wizards/AddRolesWizard";
import "../../starek.css";

const GET_ROLES = gql`
  query Roles {
    roles {
      id
      name
      description
      is_active
      memberships {
        role_id
      }
      privileges {
        id
        entity_type_id
        permission
      }
    }
  }
`;

const Roles = () => {
  const navigate = useNavigate();
  const starekContext = useContext(StarekContext);

  const { data, loading, error, refetch } = useQuery(GET_ROLES, {
    client: starekContext.engineClient,
    fetchPolicy: "network-only",
  });

  const [items, setItems] = useState([]);
  const [filterText, setFilterText] = useState("");
  const [selectedRows, setSelectedRows] = useState([]);
  const [toggleCleared, setToggleCleared] = useState(false);
  const [showWizard, setShowWizard] = useState(false);
  const [editingRole, setEditingRole] = useState(null);

  useEffect(() => {
    if (error) starekContext.showError(error);
  }, [error]);

  useEffect(() => {
    if (!loading && data?.roles) {
      setItems(data.roles || []);
    }
  }, [data, loading]);

  const filteredItems = useMemo(() => {
    const q = filterText.trim().toLowerCase();
    if (!q) return items;
    return items.filter((r) => {
      return (
        (r.name || "").toLowerCase().includes(q) ||
        (r.description || "").toLowerCase().includes(q)
      );
    });
  }, [items, filterText]);

  const isSingle = selectedRows.length === 1;

  const onClickEdit = () => {
    if (!isSingle) return;
    setEditingRole(selectedRows[0]);
    setShowWizard(true);
  };

  const onClickAdd = () => {
    setEditingRole(null);
    setShowWizard(true);
  };

  const columns = [
    {
      name: "Name",
      selector: (r) => r.name || "-",
      sortable: true,
      grow: 1.2,
      cell: (row) => <div className="text-primary">{row.name || "-"}</div>,
    },
    {
      name: "Description",
      selector: (r) => r.description || "-",
      sortable: false,
      grow: 2,
      cell: (row) => (
        <div className="text-truncate" style={{ maxWidth: 520 }}>
          {row.description || "-"}
        </div>
      ),
    },
  ];

  if (loading) {
    return (
      <>
        <Spinner />
        <div className="text-xs d-flex justify-content-center">
          <i className="fa fa-lock fa-10x text-success"></i>
        </div>
      </>
    );
  }

  return (
    <div className="container-fluid">
      <div className="d-flex align-items-center justify-content-between mb-3">
        <div
          className="d-flex align-items-center flex-grow-1"
          style={{ gap: 12 }}
        >
          <input
            type="text"
            className="border rounded p-2"
            style={{ width: "40%" }}
            placeholder="Search  —  Name, description..."
            value={filterText}
            onChange={(e) => setFilterText(e.target.value)}
          />
        </div>

        <div className="d-flex align-items-center">
          {selectedRows.length > 0 && (
            <>
              <Button
                className="me-2"
                variant={isSingle ? "outline-primary" : "secondary"}
                disabled={!isSingle}
                onClick={onClickEdit}
              >
                Edit
              </Button>

              <OverlayTrigger
                placement="top"
                overlay={
                  <Tooltip>
                    Wire your delete mutation here. After delete we clear
                    selection & refetch.
                  </Tooltip>
                }
              >
                <span className="d-inline-block me-2">
                  <Button
                    variant="secondary"
                    onClick={async () => {
                      setSelectedRows([]);
                      await refetch();
                      setToggleCleared((p) => !p);
                    }}
                  >
                    Delete
                  </Button>
                </span>
              </OverlayTrigger>
            </>
          )}

          <Button variant="primary" onClick={onClickAdd}>
            Add Role
          </Button>
        </div>
      </div>

      <DataTable
        columns={columns}
        data={filteredItems}
        selectableRows
        onSelectedRowsChange={({ selectedRows }) =>
          setSelectedRows(selectedRows)
        }
        clearSelectedRows={toggleCleared}
        highlightOnHover
        pointerOnHover
        responsive
        pagination
        dense
        selectableRowsHighlight
        selectableRowsNoSelectAll={false}
      />

      <AddRolesWizard
        show={showWizard}
        mode={editingRole ? "edit" : "add"}
        initialValues={editingRole || {}}
        onClose={() => {
          setShowWizard(false);
          setEditingRole(null);
          setToggleCleared((p) => !p);
        }}
        onFinish={async () => {
          setShowWizard(false);
          setEditingRole(null);
          await refetch();
          setToggleCleared((p) => !p);
        }}
      />
    </div>
  );
};

export { Roles };
